import Vue from 'vue'
import { Component, Mixins, Watch } from 'vue-property-decorator'
import MixinInterface from '@/_mixins/index'

import UserMixin from '@/_mixins/user'
import ProjectMixin from '@/_mixins/project'
import NotificationMixin from '@/_mixins/notification'

import Container from '@/components/Placeholders/Container/Container.vue'
import ProjectUser from '@/views/Authorization/models/projectUser'
import PersonnelProjectSettings from '@/views/Project/Personnel/models/projectSettings'
import MenuMixin from '@/_mixins/menu'

@Component({
    mixins: [UserMixin, ProjectMixin, MenuMixin, NotificationMixin],
    components: { Container },
})
export default class Project extends Mixins<MixinInterface & MenuMixin>() {
    loaded = false
    accessible = false
    error: any = null
    notificationInterval = 0
    lastUsedTimeInterval = 0

    get project() {
        return this.$route.params.type == 'p'
            ? this.$store.getters.project(this.$route.params.id)
            : null
    }

    get group() {
        return this.$route.params.type == 'g'
            ? this.$store.getters.group(this.$route.params.id)
            : null
    }

    get userSettings() {
        if (this.project && this.project.relationships.userSettings.data) {
            return this.$store.getters.projectUserSettings[
                this.project.relationships.userSettings.data.id
            ]
        } else if (this.group && this.group.relationships.userSettings.data) {
            return this.$store.getters.groupUserSettings[
                this.group.relationships.userSettings.data.id
            ]
        }
        return null
    }

    @Watch('project')
    onProjectReady(value: any, oldValue: any) {
        if (value && value.id !== oldValue?.id) {
            this.setLastUsedTime()
        }
    }

    @Watch('group')
    onGroupReady(value: any, oldValue: any) {
        if (value && value.id !== oldValue?.id) {
            this.setLastUsedTime()
        }
    }

    @Watch('isImpersonating', { immediate: true })
    async getData() {
        if (this.currentUser && this.currentUser.attributes.frontEndSettings) {
            this.$store.commit(
                'frontEndSettings',
                JSON.parse(this.currentUser.attributes.frontEndSettings)
            )
        }
    }

    @Watch('currentProjectId')
    handleProjectChange(value: string | null) {
        if (value) {
            this.checkForUnreadNotifications()
        }
    }

    goToDashboard() {
        this.$router.replace({ name: 'project-dashboard' })
    }

    checkForUnreadNotifications() {
        let id = this.isGroup ? this.currentGroup.id : this.currentProjectId

        if (!this.isAuthenticated || !id) return

        const check = async () => {
            let notificationsLastRead
            const lastReadTime = this.$store.getters.frontEndSettings.notificationsLastRead?.[id]
            if (lastReadTime && !isNaN(new Date(lastReadTime).getTime())) {
                notificationsLastRead = lastReadTime
            }

            const notificationRes = await Vue.prototype.$vbit.get('notification', 'notifications', {
                filters: {
                    'filter[read]': false,
                    'page[limit]': 1,
                    ...(this.isProject && { 'filter[project]': this.currentProjectId }),
                    ...(this.isGroup && { 'filter[projectGroup]': this.currentGroup.id }),
                    ...(notificationsLastRead && {
                        'filter[time][ge]': notificationsLastRead,
                    }),
                },
            })
            this.$store.commit('hasUnreadNotifications', notificationRes.data.length > 0)
        }
        check()
        this.notificationInterval = setInterval(check, 60 * 1000)
    }

    setTime() {
        if (!this.userSettings) {
            return
        }

        const data = {
            id: this.userSettings.id,
            type: this.userSettings.type,
            attributes: {
                lastUsed: new Date().toISOString(),
            },
        }
        if (this.isProject) {
            this.patchProjectUserSettings(data)
        } else if (this.isGroup) {
            this.patchtGroupUserSettings(data)
        }
    }

    setLastUsedTime() {
        this.setTime()
        if (this.lastUsedTimeInterval) clearInterval(this.lastUsedTimeInterval)
        this.lastUsedTimeInterval = setInterval(this.setTime, 60 * 1000)
    }

    onVisibilityChanged() {
        if (document.hidden) {
            clearInterval(this.notificationInterval)
            clearInterval(this.lastUsedTimeInterval)
        } else {
            this.checkForUnreadNotifications()
            this.setLastUsedTime()
        }
    }

    // Getting project or group info from project API, if url is wrong or user hase no access to it show Container component with error text
    async created() {
        let dashboardFilter: Record<string, any> = {}

        switch (this.$route.params.type) {
            case 'p':
                let project = await this.getProject(this.$route.params.id).catch((error: any) => {
                    this.error = error.data.response.status
                    this.loaded = true
                    this.accessible = false
                })

                if (project) {
                    dashboardFilter['filter[project]'] = project.id

                    if (this.currentUser) {
                        await Promise.all([
                            ProjectUser.getProjectUser(project.id, this.currentUser.id, true),
                            this.isModuleAccessible('personnel')
                                ? PersonnelProjectSettings.get(project.id)
                                : Promise.resolve(),
                        ]).catch((error) => {
                            this.$store.commit('closeSnackbar')
                        })
                    }
                    this.loaded = true
                    this.accessible = true
                }

                break
            case 'g':
                if (!this.isAuthenticated) {
                    this.$router.push({ name: 'sign-in', query: { url: this.$route.fullPath } })
                    return
                }
                let group = await this.getGroup(this.$route.params.id).catch((error: any) => {
                    this.error = error.data.response.status
                    this.loaded = true
                    this.accessible = false
                })
                if (group) {
                    dashboardFilter['filter[projectGroup]'] = group.id
                    this.loaded = true
                    this.accessible = true
                }
                break

            default:
                this.loaded = true
                this.accessible = false
        }

        // with current router config, below logic will only apply if previous route was project list
        if (
            Object.keys(dashboardFilter).length > 0 &&
            (this.$route.name === 'project' || this.$route.name === 'group')
        ) {
            let dashboards = await this.getDashboards(dashboardFilter)

            if (dashboards.length === 0) {
                let firstAccessibleAndActiveProjectModule: any

                for (let module of this.activeModules) {
                    let moduleId = (module as any)?.relationships?.module?.data?.id
                    let projectMenuItem = this.$$projectMenu[moduleId]
                    if (projectMenuItem && projectMenuItem.visible && !projectMenuItem.isDisabled) {
                        firstAccessibleAndActiveProjectModule = projectMenuItem
                        break
                    }
                }

                if (firstAccessibleAndActiveProjectModule) {
                    this.$router.replace(firstAccessibleAndActiveProjectModule.to)
                } else {
                    this.goToDashboard()
                }
            } else {
                this.goToDashboard()
            }
        }
    }

    mounted() {
        window.addEventListener('visibilitychange', this.onVisibilityChanged)
    }

    beforeDestroy() {
        clearInterval(this.notificationInterval)
        clearInterval(this.lastUsedTimeInterval)
        window.removeEventListener('visibilitychange', this.onVisibilityChanged)
        this.$store.commit('resetProject')
        this.$store.commit('resetAuthorization')
        this.$store.commit('resetObservationHelpers')
        this.$store.commit('resetObservation')
        this.$store.commit('resetNotification')
    }

    beforeCreate() {
        this.$store.commit('resetProject')
        this.$store.commit('resetAuthorization')
        this.$store.commit('resetObservationHelpers')
        this.$store.commit('resetObservation')
        this.$store.commit('resetNotification')
    }
}
